import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL_EMPLOYEE } from "const/constants";
import {
  dummyAdjustTimesheet,
  dummyClockIn,
  dummyClockOut,
  dummyClockStatus,
  dummyCreateEmployee,
  dummyUpdateEmployee,
  dummyMarkTimesheetsPaid,
  dummyUpdatePayment,
  dummyVerifyPin,
  getDummyEmployeeById,
  getDummyEmployees,
  getDummyTimesheets,
} from "utils/timesheetDummyData";
import { unwrapItem, unwrapList, unwrapPage } from "utils/timesheetUtils";

// TODO: set to false (and delete utils/timesheetDummyData.js) when the timesheet API is ready.
const USE_DUMMY_TIMESHEET_DATA = true;

const employeeBase = `${API_URL_EMPLOYEE}/employees`;
const timesheetBase = `${API_URL_EMPLOYEE}/timesheets`;

const getEmployeesAPI = async () => {
  if (USE_DUMMY_TIMESHEET_DATA) return getDummyEmployees();
  const response = await axios.get(employeeBase);
  return unwrapList(response?.data);
};

const getEmployeeByIdAPI = async (id) => {
  if (USE_DUMMY_TIMESHEET_DATA) return getDummyEmployeeById(id);
  const response = await axios.get(`${employeeBase}/${id}`);
  return unwrapItem(response?.data);
};

const createEmployeeAPI = async (employeeData) => {
  if (USE_DUMMY_TIMESHEET_DATA) return dummyCreateEmployee(employeeData);
  const response = await axios.post(employeeBase, employeeData);
  return unwrapItem(response?.data);
};

const updateEmployeeAPI = async ({ id, employeeData }) => {
  if (USE_DUMMY_TIMESHEET_DATA) return dummyUpdateEmployee(id, employeeData);
  const response = await axios.patch(`${employeeBase}/${id}`, employeeData);
  return unwrapItem(response?.data);
};

const deleteEmployeeAPI = async (id) => {
  const response = await axios.delete(`${employeeBase}/${id}`);
  return unwrapItem(response?.data) ?? response?.data;
};

export const verifyEmployeePIN = async (pin) => {
  if (USE_DUMMY_TIMESHEET_DATA) return dummyVerifyPin(pin);
  const response = await axios.post(`${employeeBase}/verify-pin`, { pin: String(pin).trim() });
  return unwrapItem(response?.data);
};

export const clockIn = async (employeeId, payload = {}) => {
  if (USE_DUMMY_TIMESHEET_DATA) return dummyClockIn(employeeId);
  const response = await axios.post(`${employeeBase}/${employeeId}/clock-in`, payload);
  return unwrapItem(response?.data);
};

export const clockOut = async (employeeId, payload = {}) => {
  if (USE_DUMMY_TIMESHEET_DATA) return dummyClockOut(employeeId);
  const response = await axios.post(`${employeeBase}/${employeeId}/clock-out`, payload);
  return unwrapItem(response?.data);
};

export const getEmployeeClockStatus = async (employeeId) => {
  if (USE_DUMMY_TIMESHEET_DATA) return dummyClockStatus(employeeId);
  const response = await axios.get(`${employeeBase}/${employeeId}/clock-status`);
  return unwrapItem(response?.data);
};

const getTimesheetsAPI = async (params = {}) => {
  const {
    page = 0,
    size = 50,
    search,
    employeeId,
    paymentStatus,
    fromDate,
    toDate,
    sortBy = "clockIn",
    sortDirection = "DESC",
  } = params;

  if (USE_DUMMY_TIMESHEET_DATA) return getDummyTimesheets(params);

  const response = await axios.get(timesheetBase, {
    params: {
      page,
      size,
      sortBy,
      sortDirection,
      ...(search ? { search } : {}),
      ...(employeeId ? { employeeId } : {}),
      ...(paymentStatus ? { paymentStatus } : {}),
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    },
  });
  return unwrapPage(response?.data);
};

const getTimesheetSummaryAPI = async (params = {}) => {
  if (USE_DUMMY_TIMESHEET_DATA) return null;
  const response = await axios.get(`${timesheetBase}/summary`, { params });
  return unwrapItem(response?.data);
};

const getEmployeeTimesheetStatsAPI = async ({ employeeId, fromDate, toDate } = {}) => {
  if (USE_DUMMY_TIMESHEET_DATA) return null;
  const response = await axios.get(`${employeeBase}/${employeeId}/timesheet-stats`, {
    params: {
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    },
  });
  return unwrapItem(response?.data);
};

const updateTimesheetPaymentAPI = async ({
  id,
  paymentStatus,
  paymentMethod,
  paidOn,
  paymentNote,
}) => {
  const payload = { paymentStatus, paymentMethod, paidOn, note: paymentNote, paymentNote };
  if (USE_DUMMY_TIMESHEET_DATA) {
    return dummyUpdatePayment({ id, ...payload });
  }
  const response = await axios.patch(`${timesheetBase}/${id}/payment-status`, payload);
  return unwrapItem(response?.data);
};

const markTimesheetsPaidAPI = async ({ ids, paymentMethod, paidOn, note }) => {
  const payload = { ids, paymentMethod, paidOn, note };
  if (USE_DUMMY_TIMESHEET_DATA) return dummyMarkTimesheetsPaid(payload);
  const response = await axios.post(`${timesheetBase}/mark-paid`, payload);
  return unwrapItem(response?.data) ?? response?.data;
};

const updateTimesheetEntryAPI = async ({ id, clockIn, clockOut, breakMinutes }) => {
  const payload = { clockIn, clockOut, breakMinutes };
  if (USE_DUMMY_TIMESHEET_DATA) return dummyAdjustTimesheet({ id, ...payload });
  const response = await axios.patch(`${timesheetBase}/${id}`, payload);
  return unwrapItem(response?.data);
};

export const useEmployees = (options = {}) =>
  useQuery({
    queryKey: ["employees"],
    queryFn: getEmployeesAPI,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useEmployeeById = (id, options = {}) =>
  useQuery({
    queryKey: ["employees", id],
    queryFn: () => getEmployeeByIdAPI(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useCreateEmployee = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...rest } = options;
  return useMutation({
    mutationFn: createEmployeeAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      onSuccess?.(data, variables, context);
    },
    onError,
    ...rest,
  });
};

export const useUpdateEmployee = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...rest } = options;
  return useMutation({
    mutationFn: updateEmployeeAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employees", variables.id] });
      onSuccess?.(data, variables, context);
    },
    onError,
    ...rest,
  });
};

export const useDeleteEmployee = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...rest } = options;
  return useMutation({
    mutationFn: deleteEmployeeAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
      onSuccess?.(data, variables, context);
    },
    onError,
    ...rest,
  });
};

export const useTimesheets = (params = {}, options = {}) =>
  useQuery({
    queryKey: ["timesheets", params],
    queryFn: () => getTimesheetsAPI(params),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useTimesheetSummary = (params = {}, options = {}) =>
  useQuery({
    queryKey: ["timesheets-summary", params],
    queryFn: () => getTimesheetSummaryAPI(params),
    refetchOnWindowFocus: false,
    ...options,
  });

export const useEmployeeTimesheetStats = (employeeId, params = {}, options = {}) =>
  useQuery({
    queryKey: ["employee-timesheet-stats", employeeId, params],
    queryFn: () => getEmployeeTimesheetStatsAPI({ employeeId, ...params }),
    enabled: !!employeeId,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useUpdateTimesheetEntry = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...rest } = options;
  return useMutation({
    mutationFn: updateTimesheetEntryAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
      queryClient.invalidateQueries({ queryKey: ["timesheets-summary"] });
      queryClient.invalidateQueries({ queryKey: ["employee-timesheet-stats"] });
      onSuccess?.(data, variables, context);
    },
    onError,
    ...rest,
  });
};

export const useMarkTimesheetsPaid = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...rest } = options;
  return useMutation({
    mutationFn: markTimesheetsPaidAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
      queryClient.invalidateQueries({ queryKey: ["timesheets-summary"] });
      queryClient.invalidateQueries({ queryKey: ["employee-timesheet-stats"] });
      onSuccess?.(data, variables, context);
    },
    onError,
    ...rest,
  });
};

export const useUpdateTimesheetPayment = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...rest } = options;
  return useMutation({
    mutationFn: updateTimesheetPaymentAPI,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
      queryClient.invalidateQueries({ queryKey: ["timesheets-summary"] });
      queryClient.invalidateQueries({ queryKey: ["employee-timesheet-stats"] });
      onSuccess?.(data, variables, context);
    },
    onError,
    ...rest,
  });
};

export const getEmployees = getEmployeesAPI;
export const getEmployeeById = getEmployeeByIdAPI;
export const createEmployee = createEmployeeAPI;
export const updateEmployee = (id, employeeData) => updateEmployeeAPI({ id, employeeData });
export const deleteEmployee = deleteEmployeeAPI;
